import type {QueryClient} from '@tanstack/react-query'

import {apiGetPaginatedItems} from '../../../api/memex-items/api-get-paginated-items'
import type {GetPaginatedItemsRequest, GetPaginatedItemsResponse} from '../../../api/memex-items/paginated-views'
import type {GroupIdToGroupValueMap} from '../query-client-api/memex-groups'
import {handlePaginatedItemsResponse} from './handle-paginated-items-response'
import {isPageTypeForGroupedItems, type PageType} from './query-keys'
import type {
  MemexGroupsPageQueryData,
  MemexItemsPageQueryData,
  PageParam,
  pageParamForNextPlaceholder,
  PaginatedMemexItemsQueryVariables,
} from './types'

export function paginatedViewRequestResolverFactory(
  variables: PaginatedMemexItemsQueryVariables,
  groupValuesMap: GroupIdToGroupValueMap,
  pageType: PageType,
  pageParam: Exclude<PageParam, typeof pageParamForNextPlaceholder>,
) {
  return new PaginatedViewsRequestResolver(variables, groupValuesMap, pageType, pageParam)
}

class PaginatedViewsRequestResolver {
  protected _variables: PaginatedMemexItemsQueryVariables
  protected _groupValuesMap: GroupIdToGroupValueMap
  protected _pageType: PageType
  protected _pageParam: Exclude<PageParam, typeof pageParamForNextPlaceholder>

  protected _queryData: MemexItemsPageQueryData | MemexGroupsPageQueryData
  protected _request: GetPaginatedItemsRequest
  protected _response: GetPaginatedItemsResponse

  constructor(
    variables: PaginatedMemexItemsQueryVariables,
    groupValuesMap: GroupIdToGroupValueMap,
    pageType: PageType,
    pageParam: Exclude<PageParam, typeof pageParamForNextPlaceholder>,
  ) {
    this._variables = variables
    this._groupValuesMap = groupValuesMap
    this._pageType = pageType
    this._pageParam = pageParam
    this._request = {...this.buildRequest(), ...this._pageParam}
  }

  private buildRequest() {
    return buildPaginatedItemsRequest(this._variables, this._groupValuesMap, this._pageType, this._pageParam)
  }

  public async fetchData(signal?: AbortSignal) {
    this._response = await apiGetPaginatedItems(this._request, signal)
    return this._response
  }

  public handleResponse(queryClient: QueryClient) {
    this._queryData = handlePaginatedItemsResponse(
      queryClient,
      this._variables,
      this._response,
      this._pageType,
    ).queryData
  }

  public queryData() {
    return this._queryData
  }
}

export function buildPaginatedItemsRequest(
  variables: PaginatedMemexItemsQueryVariables,
  groupValuesMap: GroupIdToGroupValueMap,
  pageType: PageType,
  pageParam: Exclude<PageParam, typeof pageParamForNextPlaceholder>,
) {
  const paginatedFetchOptions: GetPaginatedItemsRequest = {...variables, ...pageParam}
  if (isPageTypeForGroupedItems(pageType)) {
    const groupValue = groupValuesMap[pageType.groupId]
    if (variables.verticalGroupedByColumnId && variables.horizontalGroupedByColumnId && pageType.secondaryGroupId) {
      // we are requesting a cell of data for swimlanes
      const secondaryGroupValue = groupValuesMap[pageType.secondaryGroupId]
      paginatedFetchOptions.verticalGroupedByGroupValue = groupValue
      paginatedFetchOptions.groupedByGroupValue = secondaryGroupValue
    } else if (variables.verticalGroupedByColumnId) {
      paginatedFetchOptions.verticalGroupedByGroupValue = groupValue
    } else if (variables.horizontalGroupedByColumnId) {
      paginatedFetchOptions.groupedByGroupValue = groupValue
    }
  }
  return paginatedFetchOptions
}
